import { Injectable } from '@angular/core'
import { firstValueFrom, Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { FirestoreService2 } from './firestore.service2'
import { Region, PostcodeRange } from './shipping.interface'

@Injectable({ providedIn: 'root' })
export class RegionService {
  private cachedRegions: Region[] | null = null

  constructor(private fss2: FirestoreService2) {
  }

  /** Get all regions */
  getRegions(): Observable<Region[]> {
    return this.fss2.list('regions').pipe(
      map(regions => regions || [])
    )
  }

  /** Invalidate the cached regions (call after admin changes) */
  invalidateRegionsCache(): void {
    this.cachedRegions = null
  }

  /** Get a single region by id */
  getRegion(regionId: string): Observable<Region | null> {
    return this.fss2.get('regions', regionId)
  }

  /** Create a new region */
  async createRegion(region: Omit<Region, 'createdAt' | 'updatedAt'>): Promise<string> {
    const now = new Date()
    const data = {
      ...region,
      createdAt: now,
      updatedAt: now
    }
    const ref = await this.fss2.add('regions', data)
    this.invalidateRegionsCache()
    return ref.id
  }

  /** Update an existing region */
  async updateRegion(regionId: string, data: Partial<Region>): Promise<void> {
    await this.fss2.update('regions', regionId, {
      ...data,
      updatedAt: new Date()
    })
    this.invalidateRegionsCache()
  }

  /** Delete a region */
  async deleteRegion(regionId: string): Promise<void> {
    await this.fss2.delete('regions', regionId)
    this.invalidateRegionsCache()
  }

  /** Get all regions as a promise (useful for one-shot lookups). Results are cached in-memory. */
  async getRegionsOnce(): Promise<Region[]> {
    if (this.cachedRegions) return this.cachedRegions
    this.cachedRegions = await firstValueFrom(this.getRegions())
    return this.cachedRegions
  }

  /** Validate a region config */
  validateRegion(region: Partial<Region>): string[] {
    const errors: string[] = []

    if (!region.countries?.length) errors.push('countries must not be empty')

    if (region.postcodeRanges?.length) {
      for (const range of region.postcodeRanges) {
        if (range.from > range.to) {
          errors.push(`postcode range from "${range.from}" > to "${range.to}"`)
        }
        if (!range.zone) {
          errors.push('postcode range must have a zone')
        }
      }
    }

    if (region.services) {
      const defaultCount = Object.values(region.services).filter(s => s.isDefault).length
      if (defaultCount === 0) errors.push('at least one service must be default')
      if (defaultCount > 1) errors.push('only one service can be default')
    }

    return errors
  }

  // ═══════════════════════════════════════════════════════════════════
  // Postal Code Matching
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Normalize a postal code: strip hyphens, spaces; uppercase; truncate to 4 digits (district prefix).
   * E.g. '1000-001' → '1000', '9499-999' → '9499'
   */
  private normalize(postalCode: string): string {
    return postalCode.replace(/[-\s]/g, '').substring(0, 4)
  }

  /**
   * Match a postal code + country against a single region.
   * Checks country membership and postcode range inclusion.
   */
  private matchRegion(postalCode: string, country: string, region: Region): boolean {
    if (!region.countries.includes(country)) return false
    const normalized = this.normalize(postalCode)
    return region.postcodeRanges.some(
      r => normalized >= r.from && normalized <= r.to
    )
  }

  /**
   * Determine which zone (Continental, Madeira, Açores, etc.) a postcode belongs to.
   * Returns the zone name or null if no match.
   */
  getZoneForPostcode(postalCode: string, country: string, region: Region): string | null {
    if (!region.countries.includes(country)) return null
    const normalized = this.normalize(postalCode)
    const range = region.postcodeRanges.find(
      r => normalized >= r.from && normalized <= r.to
    )
    return range?.zone || null
  }

  /**
   * Find the first matching enabled region from a list.
   * Returns null if no region matches.
   */
  findRegion(postalCode: string, country: string, regions: Region[]): Region | null {
    return regions.find(r => r.enabled && this.matchRegion(postalCode, country, r)) || null
  }

  /**
   * Resolve country + postcode from user data (cartMeta, addresses).
   * Priority: cartMeta → primaryAddress → default deliveryAddress → fallback 'PT'
   */
  resolveShippingLocation(user: any): { country: string; postcode: string | null } {
    const country =
      user.cartMeta?.shippingCountry
      || user.primaryAddress?.country
      || user.deliveryAddresses?.find((a: any) => a.isDefault)?.country
      || 'PT'

    const postcode =
      user.cartMeta?.shippingPostcode
      || user.primaryAddress?.postalCode
      || user.deliveryAddresses?.find((a: any) => a.isDefault)?.postalCode
      || null

    return { country, postcode }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Seller Exclusion by Postcode Zone
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Returns the list of excluded sellers (lowercase) for a given postcode,
   * by matching it against all active regions' postcodeRanges.
   * Returns an empty array when no postcode is provided or no range matches.
   */
  getExcludedSellers(postcode: string, regions: Region[]): string[] {
    if (!postcode || !regions?.length) return []
    const normalized = this.normalize(postcode)
    for (const region of regions) {
      const range = region.postcodeRanges?.find(r => normalized >= r.from && normalized <= r.to)
      if (range?.excludedSellers?.length) {
        return range.excludedSellers.map(s => s.toLowerCase())
      }
    }
    return []
  }

  /**
   * Filters out products whose seller is in the excluded list for the user's postcode.
   * When no postcode is available, returns the original array unchanged.
   */
  filterProducts<T extends { seller?: string }>(products: T[], postcode: string, regions: Region[]): T[] {
    const excluded = this.getExcludedSellers(postcode, regions)
    if (!excluded.length) return products
    return products.filter(p => !excluded.includes(p.seller?.toLowerCase()))
  }

  filterCategories<T extends { seller?: string }>(categories: T[], postcode: string, regions: Region[]): T[] {
    const excluded = this.getExcludedSellers(postcode, regions)
    if (!excluded.length) return categories
    return categories.filter(c => !c.seller || !excluded.includes(c.seller.toLowerCase()))
  }
}
